import React, { FC } from 'react';
import { DateField, RichText, Text } from '@sitecore-jss/sitecore-jss-react';
import { BreakpointsInPx } from '../../../constants/breakpoints.constants';
import DictionaryTerm from '../../@shared/dictionaryTerm';
import SocialMediaBar from '../../@shared/socialMediaBar';
import EventDetailMedia from './components/media';
import { EventDetailModel } from './models/eventDetail.model';
import { TEventDetailProps } from './types/eventDetail.types';
import useEEActive from '../../../hooks/useEEActive';
import useFormatDate from '../../../hooks/useFormatDate';
import useMediaQuery from '../../../lib/custom-hooks/useMediaQuery';
import CTA, { ctaTypes } from '../cta';
import { EVENT_TITLE_DICTIONARY_KEY, EVENTS_TEMPLATE_ID } from '../../../constants/eventsFairs.constants';
import { targetLinkdsk } from '../../../utils/targetDskLink';
import useSitecoreContext from '../../../hooks/useSitecoreContext';
import SaveCalendarEvent from '../saveCalendarEvent';

const EventDetailContainer: FC<TEventDetailProps> = (props) => {
  const model = new EventDetailModel(props);
  const isEE = useEEActive();
  const isDesktop = useMediaQuery(`(min-width: ${BreakpointsInPx['tbl-p']})`);
  const formattedStartDate = useFormatDate(model.startDate?.value);
  const formattedEndDate = useFormatDate(model.endDate?.value);
  const context = useSitecoreContext();
  const templateId = context?.sitecoreContext?.route?.templateId;

  return (
    <section className="event-detail__wrapper">
      <div className="event-detail">
        <div className="event-detail__header">
          <p className={'event-detail__eyebrow'}>
          <DictionaryTerm
              keyword={
                templateId === EVENTS_TEMPLATE_ID.EVENT ? EVENT_TITLE_DICTIONARY_KEY.EVENT : EVENT_TITLE_DICTIONARY_KEY.LIVE_EVENT
              }
            />
          </p>
          {model?.title && <Text field={model.title} tag="h1" className={'event-detail__title'} />}
          <div>
            {(model.canShowStartDate || isEE) && (
              <DateField field={model.startDate} tag="p" className="event-detail__date" render={() => formattedStartDate} />
            )}
            {((model.canShowStartDate && model.canShowEndDate) || isEE) && (
              <>
                <p className="event-detail__separator">&nbsp;-&nbsp;</p>
                <DateField field={model.endDate} tag="p" className="event-detail__date" render={() => formattedEndDate} />
              </>
            )}
            {model?.time?.value && <p className="event-detail__separator">&nbsp;|&nbsp;</p>}
            {model?.time && <Text field={model.time} tag="p" className={'event-detail__time'} />}
            {model.location?.value && <p className="event-detail__separator">&nbsp;|&nbsp;</p>}
            {model?.location && <Text field={model.location} tag="p" className={'event-detail__location'} />}
          </div>
          <div className="event-detail__share-save">
          {model.socialMediaBarProps && <SocialMediaBar {...model.socialMediaBarProps} extraClassName="event-detail__social-media-bar" useAnalytics/>}
          <SaveCalendarEvent
              extraClassName={'event-detail__save-event'}
              calendarDetails={model.calendarDetails}
              startDate={model.startDate?.value}
              endDate={model.endDate?.value}
            />
          </div>
        </div>

        <div className={`event-detail__media-wrapper ${!model?.mediaExists ? 'event-detail__media--no-media' : ''}`}>
          <EventDetailMedia
            showVideo={model?.showVideo}
            desktopImage={model?.desktopImage}
            mobileImage={model?.mobileImage}
            video={model?.video}
            isMediaExists={model?.mediaExists}
          />
        </div>

        {model.detailedDescription && (
          <RichText tag="div" field={model.detailedDescription} className={'event-detail__description rich-text rich-text-anchor'} />
        )}

        {(isEE || model.showCtaWrapper) && (
          <div className="event-detail__cta-wrapper">
            {(isEE || model.showPrimaryCta) && (
              <CTA
              type={ctaTypes.primaryRed}
                extraClassName="event-detail__cta-primary"
                field={model.primaryCta}
                target={targetLinkdsk(model?.primaryCta, isDesktop)}
              />
            )}
            {(isEE || model.showSecondaryCta) && (
              <CTA
                type={ctaTypes.secondaryRed}
                field={model.secondaryCta}
                target={targetLinkdsk(model?.primaryCta, isDesktop)}
                extraClassName={'event-detail__cta-secondary'}
              />
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default EventDetailContainer;
